import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LifeBuoy, Search, Filter, Loader2, Send, ArrowLeft, CheckCircle2, AlertCircle,
  Paperclip, User as UserIcon, Shield, Clock, IndianRupee, Plus, Undo2, X,
  FileText, FilePlus, Lock,
} from 'lucide-react';
import { BookingSummaryCard, PaymentsPanel } from '../MyTickets';
import { supportAttachmentHref } from '../../utils/supportAttachmentHref';
import { resolveOpsBookingKeys } from '../../utils/userFacingBookingId';
import {
  REFUND_PAYMENT_METHOD_LABELS,
  resolveRefundRouting,
} from '../../utils/supportRefundRouting';
import {
  makeOptimisticMessage,
  maxMessageCursor,
  maxQueueCursor,
  mergeQueueTickets,
  mergeThreadMessages,
  SUPPORT_QUEUE_FULL_RELOAD_MS,
  useSupportQueuePoll,
  useSupportTabTitle,
  useSupportThreadRealtime,
  useThreadScroll,
} from '../../utils/supportThreadRealtime';
import { formatSupportSenderLabel } from '../../utils/supportSenderLabel';
import AdminTicketBookingOpsPanel from './AdminTicketBookingOpsPanel';
import SupportAssignmentSettings from './SupportAssignmentSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Roles allowed to assign support tickets (P0 — ops manager + super admin). */
const SUPPORT_ASSIGN_ROLES = new Set(['super_admin', 'ops_manager']);
const SUPPORT_REFUND_ROLES = new Set(['super_admin', 'ops_manager', 'accounts']);
const OPS_EXECUTIVE_ROLE = 'ops_executive';

function roundRefundAmount(n) {
  return Math.round(n * 100) / 100;
}

/** Paid-with / refund-to panel (P0 payment source clarity). */
const RefundRoutingPanel = ({ routing }) => {
  if (!routing) return null;
  return (
    <div className="border border-gray-200 rounded-xl p-3 space-y-2 text-xs" data-testid="refund-routing-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <p className="text-[10px] uppercase font-bold text-gray-500 mb-0.5">Paid with</p>
          <p className="font-semibold text-gray-900" data-testid="refund-paid-with">
            {routing.paid_with_label}
          </p>
          {routing.payment_mode_raw && routing.payment_mode && String(routing.payment_mode_raw).toUpperCase() !== routing.payment_mode && (
            <p className="text-[10px] text-gray-500 mt-0.5">Raw: {routing.payment_mode_raw}</p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-gray-500 mb-0.5">Refund to</p>
          <p className={`font-semibold ${routing.instant ? 'text-emerald-800' : 'text-amber-800'}`} data-testid="refund-to-label">
            {routing.refund_to_label}
          </p>
          {routing.instant && (
            <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">Instant</span>
          )}
          {routing.queued_pg && (
            <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">PG queue</span>
          )}
        </div>
      </div>
      {routing.routing_note && (
        <p className="text-[11px] text-gray-600 leading-snug">{routing.routing_note}</p>
      )}
      {routing.payment_source_unknown && (
        <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="refund-source-unknown">
          Payment source is not recorded on this booking. Refund will queue in PG Refund Tracker unless you verify wallet/credit/gateway first.
        </p>
      )}
    </div>
  );
};

const STATUS_TABS = [
  { id: 'unread', label: 'Unread', tone: 'bg-red-100 text-red-700' },
  { id: 'open', label: 'Open', tone: 'bg-blue-100 text-blue-700' },
  { id: 'in_process', label: 'In Process', tone: 'bg-amber-100 text-amber-700' },
  { id: 'need_reply', label: 'Need Reply', tone: 'bg-purple-100 text-purple-700' },
  { id: 'closed', label: 'Closed', tone: 'bg-slate-100 text-slate-600' },
];

const CATEGORIES = {
  cancel_refund: 'Cancel / Refund',
  date_change: 'Date Change',
  name_correction: 'Name Correction',
  seat_selection: 'Seat Selection',
  add_baggage: 'Add Baggage',
  add_meal: 'Add Meal',
  special_request: 'Special Request',
  gst_invoice: 'GST / Invoice',
  payment_wallet: 'Payment / Wallet',
  schedule_change: 'Schedule Change',
  failed_booking: 'Failed Booking',
  reprice: 'Reprice',
  supplier_escalation: 'Supplier Escalation',
  group_booking: 'Group Booking',
  seat_block: 'Seat Block',
  general_inquiry: 'General Inquiry',
  other: 'Other',
};

const PRIORITY_COLORS = {
  normal: 'text-gray-600',
  urgent: 'text-orange-600 font-semibold',
  critical: 'text-red-600 font-bold',
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  QUEUE VIEW
 * ═══════════════════════════════════════════════════════════════════════════ */
export const SupportQueue = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('unread');
  const [category, setCategory] = useState('all');
  const [priority, setPriority] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [assignedTo, setAssignedTo] = useState('all');
  const [assignees, setAssignees] = useState([]);
  const [adminMe, setAdminMe] = useState(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, counts: {}, tickets: [] });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [bookingIdFilter, setBookingIdFilter] = useState('');
  const queueSinceRef = useRef('');

  const canAssignTickets = SUPPORT_ASSIGN_ROLES.has(adminMe?.role);
  const isOpsExecutive = adminMe?.role === OPS_EXECUTIVE_ROLE;
  const myAdminId = adminMe?.id || '';

  useEffect(() => {
    axios.get(`${API}/admin/me`, { withCredentials: true })
      .then((r) => {
        setAdminMe(r.data);
        if (r.data?.role === OPS_EXECUTIVE_ROLE && r.data?.id) {
          setAssignedTo(r.data.id);
        }
      })
      .catch(() => setAdminMe(null));
    axios.get(`${API}/admin/support/assignees`, { withCredentials: true })
      .then((r) => setAssignees(r.data.admins || []))
      .catch(() => setAssignees([]));
  }, []);

  useEffect(() => {
    const bid = new URLSearchParams(window.location.search).get('booking_id');
    if (bid) setBookingIdFilter(bid);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await axios.get(`${API}/admin/support/queue`, {
        withCredentials: true,
        params: {
          status: tab,
          category,
          priority,
          search: searchDebounced,
          page,
          limit: 25,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          assigned_to: assignedTo,
          booking_id: bookingIdFilter || undefined,
        },
      });
      setData(r.data);
      queueSinceRef.current = maxQueueCursor(r.data.tickets, new Date().toISOString());
    } catch (e) {
      setData({ total: 0, counts: {}, tickets: [] });
      setLoadError(e.response?.data?.detail || 'Could not load queue');
    }
    setLoading(false);
  }, [tab, category, priority, searchDebounced, page, dateFrom, dateTo, assignedTo, bookingIdFilter]);

  const queuePollParams = useMemo(() => ({
    category,
    priority,
    search: searchDebounced || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    assigned_to: assignedTo,
    booking_id: bookingIdFilter || undefined,
  }), [category, priority, searchDebounced, dateFrom, dateTo, assignedTo, bookingIdFilter]);

  const handleQueuePoll = useCallback((payload) => {
    if (payload?.counts) {
      setData((prev) => ({
        ...prev,
        counts: payload.counts,
        tickets: payload.tickets?.length
          ? mergeQueueTickets(prev.tickets, payload.tickets)
          : prev.tickets,
      }));
    }
    if (payload?.tickets?.length || payload?.server_time) {
      queueSinceRef.current = maxQueueCursor(
        payload.tickets,
        payload.server_time,
      ) || queueSinceRef.current;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useSupportQueuePoll({
    enabled: !loading && !loadError,
    pollUrl: `${API}/admin/support/queue/poll`,
    pollParams: queuePollParams,
    getSince: () => queueSinceRef.current,
    onPollResult: handleQueuePoll,
  });

  useEffect(() => {
    const iv = setInterval(() => { load(); }, SUPPORT_QUEUE_FULL_RELOAD_MS);
    return () => clearInterval(iv);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(data.total / 25));

  const fmtFirstReply = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <LifeBuoy className="w-7 h-7 text-orange-500" /> Support Queue
          </h1>
          <p className="text-sm text-gray-500 mt-1">Tickets raised by B2C users & agents</p>
        </div>
        <div className="text-right text-sm text-gray-500">
          <div>Total: <strong className="text-gray-900">{data.counts.all || 0}</strong></div>
          <div className="text-[11px] text-gray-400 mt-0.5">Tab counts respect filters below</div>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-1 mb-5 bg-white border border-gray-200 rounded-xl p-1 w-fit">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`support-tab-${t.id}`}
            onClick={() => { setTab(t.id); setPage(1); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              tab === t.id
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.label}
            <span className={`text-xs px-1.5 py-0.5 rounded ${tab === t.id ? 'bg-white/20 text-white' : t.tone}`}>
              {data.counts[t.id] || 0}
            </span>
          </button>
        ))}
      </div>

      {loadError && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{loadError}</div>
      )}

      <SupportAssignmentSettings canEdit={canAssignTickets} />

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5" data-testid="filter-category">
          <option value="all">All Categories</option>
          {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5" data-testid="filter-priority">
          <option value="all">All Priorities</option>
          <option value="normal">Normal</option>
          <option value="urgent">Urgent</option>
          <option value="critical">Critical</option>
        </select>
        {!isOpsExecutive && (
          <select value={assignedTo} onChange={(e) => { setAssignedTo(e.target.value); setPage(1); }}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5" data-testid="filter-assigned">
            <option value="all">All assignees</option>
            <option value="unassigned">Unassigned</option>
            {assignees.map((a) => (
              <option key={a.id} value={a.id}>{a.name || a.email || a.id} (Ops Executive)</option>
            ))}
          </select>
        )}
        {canAssignTickets && (
          <button
            type="button"
            onClick={() => { setAssignedTo('unassigned'); setPage(1); }}
            className="text-xs px-2 py-1.5 rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-50 font-semibold"
            data-testid="filter-unassigned"
          >
            Unassigned
          </button>
        )}
        {myAdminId && (
          <button
            type="button"
            onClick={() => { setAssignedTo(myAdminId); setPage(1); }}
            className={`text-xs px-2 py-1.5 rounded-lg border font-semibold ${
              assignedTo === myAdminId
                ? 'border-orange-500 bg-orange-50 text-orange-800'
                : 'border-orange-300 text-orange-700 hover:bg-orange-50'
            }`}
            data-testid="filter-mine"
          >
            My tickets
          </button>
        )}
        {isOpsExecutive && (
          <span className="text-[11px] text-gray-500">Showing your assigned queue</span>
        )}
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span>From</span>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                 className="border border-gray-300 rounded-lg px-2 py-1" data-testid="filter-date-from" />
          <span>To</span>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                 className="border border-gray-300 rounded-lg px-2 py-1" data-testid="filter-date-to" />
        </div>
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="filter-search"
            type="text"
            placeholder="Ref · PO · name · email · phone"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-full"
          />
        </div>
      </div>

      {!loading && data.tickets.length > 0 && (
        <div className="md:hidden space-y-2 mb-4">
          {data.tickets.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => navigate(`/admin/support/${t.id}`)}
              className="w-full text-left bg-white border border-gray-200 rounded-xl p-3"
            >
              <div className="flex justify-between items-start gap-2">
                <span className="font-mono text-xs text-blue-600">{t.reference}</span>
                <StatusBadge status={t.status} />
              </div>
              <p className="text-sm font-medium mt-1 truncate">{t.subject || CATEGORIES[t.category]}</p>
              <p className="text-xs text-gray-500 mt-1">{t.created_by_name} · {CATEGORIES[t.category] || t.category}</p>
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hidden md:block">
        {loading ? (
          <div className="p-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : data.tickets.length === 0 ? (
          <div className="p-16 text-center text-gray-500 text-sm">No tickets in this view.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-600">Reference</th>
                  <th className="px-4 py-2 font-medium text-gray-600">PO Ref</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Category</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Subject</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Created By</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Assigned</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Priority</th>
                  <th className="px-4 py-2 font-medium text-gray-600">First reply</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Last Message</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.tickets.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/admin/support/${t.id}`)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    data-testid={`ticket-row-${t.id}`}
                  >
                    <td className="px-4 py-2 font-mono text-xs text-blue-600">{t.reference}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">{t.po_reference || '—'}</td>
                    <td className="px-4 py-2">{CATEGORIES[t.category] || t.category}</td>
                    <td className="px-4 py-2 max-w-[280px] truncate">{t.subject || '—'}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium">{t.created_by_name}</div>
                      <div className="text-xs text-gray-500 capitalize">{t.created_by_type}</div>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-700">{t.assigned_to_name || '—'}</td>
                    <td className={`px-4 py-2 capitalize ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</td>
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">{fmtFirstReply(t.first_admin_reply_at)}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={t.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
            <span className="text-gray-500">Page {page} / {totalPages} · {data.total} tickets</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
              <button type="button" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                      className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    unread: 'bg-red-100 text-red-700',
    open: 'bg-blue-100 text-blue-700',
    in_process: 'bg-amber-100 text-amber-700',
    need_reply: 'bg-purple-100 text-purple-700',
    closed: 'bg-slate-100 text-slate-600',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${map[status] || 'bg-gray-100'}`}>{status?.replace('_', ' ')}</span>;
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  TICKET DETAIL
 * ═══════════════════════════════════════════════════════════════════════════ */
export const SupportTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [closeForm, setCloseForm] = useState({
    refund_amount: '',
    refund_reference: '',
    refund_method: 'offline_manual',
    notes: '',
  });
  // Payment request + refund modals
  const [showPayReq, setShowPayReq] = useState(false);
  const [payReqForm, setPayReqForm] = useState({ amount: '', notes: '' });
  const [payReqSubmitting, setPayReqSubmitting] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [refundStep, setRefundStep] = useState('form');
  const [refundForm, setRefundForm] = useState({
    amount: '',
    reference: '',
    notes: '',
    close_ticket: true,
    resolution_type: '',
    resolution_remark: '',
    cancel_scope: 'full',
    selected_pax: [],
    supplier_cancel_fee: '',
  });
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundToast, setRefundToast] = useState(null);

  // GST documents (Phase 2D.2)
  const [showCnModal, setShowCnModal] = useState(false);
  const [cnForm, setCnForm] = useState({ cancel_fee_per_pax: '', pax_count: '', reason: '', airline_refund: '' });
  const [cnSubmitting, setCnSubmitting] = useState(false);
  const [showSuppModal, setShowSuppModal] = useState(false);
  const [suppForm, setSuppForm] = useState({ service_fee_per_pax: '', pax_count: '', description: '',
                                               parent_type: 'reschedule', airline_charge: '' });
  const [suppSubmitting, setSuppSubmitting] = useState(false);

  const [assignees, setAssignees] = useState([]);
  const [adminMe, setAdminMe] = useState(null);
  const [assignValue, setAssignValue] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [internalSending, setInternalSending] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [inboundPing, setInboundPing] = useState(0);
  const sinceRef = useRef('');
  const threadPaused = showClose || showRefund || showPayReq || showCnModal || showSuppModal;
  const { threadRef, onThreadScroll, scrollToBottom, newCount } = useThreadScroll(data?.messages);

  const canAssignTickets = SUPPORT_ASSIGN_ROLES.has(adminMe?.role);
  const canProcessRefund = SUPPORT_REFUND_ROLES.has(adminMe?.role);
  const isOpsExecutive = adminMe?.role === OPS_EXECUTIVE_ROLE;
  const myAdminId = adminMe?.id || '';
  const t = data?.ticket;
  const canSelfClaim = isOpsExecutive && t && !t.assigned_to;
  const isAssignedToMe = isOpsExecutive && t?.assigned_to === myAdminId;

  const claimTicket = async () => {
    if (!myAdminId) return;
    try {
      await axios.put(
        `${API}/admin/support/tickets/${id}/assign`,
        { admin_id: myAdminId },
        { withCredentials: true },
      );
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not claim ticket');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setDetailError('');
    try {
      const r = await axios.get(`${API}/admin/support/tickets/${id}`, { withCredentials: true });
      setData(r.data);
      sinceRef.current = maxMessageCursor(r.data?.messages || []);
    } catch (e) {
      setData(null);
      setDetailError(e.response?.data?.detail || 'Could not load ticket');
    }
    setLoading(false);
  }, [id]);

  const handleThreadPoll = useCallback((payload) => {
    const incoming = payload?.messages || [];
    if (incoming.some((m) => m.sender_type !== 'admin' && m.sender_type !== 'internal')) {
      setInboundPing((p) => p + 1);
    }
    setData((prev) => {
      if (!prev) return prev;
      const merged = mergeThreadMessages(prev.messages, incoming);
      sinceRef.current = maxMessageCursor(merged);
      const ticket = payload?.ticket ? { ...prev.ticket, ...payload.ticket } : prev.ticket;
      return { ...prev, messages: merged, ticket };
    });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setInboundPing(0); }, [id]);

  useSupportThreadRealtime({
    ticketId: id,
    streamUrl: id ? `${API}/admin/support/tickets/${id}/stream` : '',
    pollUrl: id ? `${API}/admin/support/tickets/${id}/messages` : '',
    enabled: Boolean(!loading && data?.ticket),
    paused: threadPaused,
    getSince: () => sinceRef.current,
    onPollResult: handleThreadPoll,
  });

  useSupportTabTitle({
    enabled: Boolean(data?.ticket),
    ticketRef: data?.ticket?.reference,
    ticketStatus: data?.ticket?.status,
    inboundPing,
  });

  useEffect(() => {
    axios.get(`${API}/admin/me`, { withCredentials: true })
      .then((r) => setAdminMe(r.data))
      .catch(() => setAdminMe(null));
    axios.get(`${API}/admin/support/assignees`, { withCredentials: true })
      .then((r) => setAssignees(r.data.admins || []))
      .catch(() => setAssignees([]));
  }, []);

  useEffect(() => {
    if (data?.ticket) setAssignValue(data.ticket.assigned_to || '');
  }, [data?.ticket?.assigned_to, data?.ticket?.id]);

  const isAgent = data?.ticket?.created_by_type === 'agent';
  const refundRouting = useMemo(
    () => resolveRefundRouting({
      apiRouting: data?.refund_routing,
      isAgent,
      booking: data?.booking,
      snap: data?.ticket?.booking_snapshot,
    }),
    [data?.refund_routing, data?.booking, data?.ticket?.booking_snapshot, isAgent],
  );
  const expectedRefundMethod = refundRouting?.expected_method || refundRouting?.refund_method || 'offline_manual';
  const paymentMode = refundRouting?.payment_mode
    || data?.booking?.payment_mode
    || data?.ticket?.booking_snapshot?.payment_mode;
  const remainingRefundable = Number(data?.refund_ledger?.remaining_refundable ?? 0);
  const refundCap = Number(data?.refund_ledger?.cap ?? 0);
  const refundAmountNum = parseFloat(refundForm.amount || 0) || 0;
  const refundOverRemaining = Boolean(data?.refund_ledger && refundAmountNum > remainingRefundable + 0.01);
  const refundNothingLeft = Boolean(data?.refund_ledger && remainingRefundable <= 0);
  const refundFormInvalid = !refundAmountNum || refundAmountNum <= 0 || refundOverRemaining || refundNothingLeft;
  const pgRefundPending = data?.refund_tracker?.refund_status === 'pending_gateway';
  const priorRefunds = useMemo(() => {
    const rows = (data?.payments || []).filter((p) => p.type === 'REFUND');
    return [...rows]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 3);
  }, [data?.payments]);
  const suggestedRefundMethod = useMemo(() => {
    if (!isAgent) return 'offline_manual';
    if (paymentMode === 'CREDIT') return 'credit_ledger';
    if (paymentMode === 'WALLET' || paymentMode === 'CASH') return 'wallet_credit';
    return 'offline_manual'; // Payment gateway → manual processing
  }, [isAgent, paymentMode]);

  const suggestedResolutionType = useMemo(() => {
    const cat = data?.ticket?.category;
    const b = data?.booking || {};
    const snap = data?.ticket?.booking_snapshot || {};
    if (cat === 'failed_booking') return 'issuance_failure';
    const ts = String(b.ticket_status || snap.ticket_status || '').toLowerCase();
    const issued = ['ticketed', 'ticket_issued', 'issued', 'confirmed'].includes(ts)
      || Boolean(b.pnr && ts && !['pending', 'failed', 'pending_issuance'].includes(ts));
    if (issued || cat === 'cancel_refund') return 'post_ticket_cancel';
    const paid = ['paid', 'completed', 'captured'].includes(String(b.payment_status || '').toLowerCase());
    if (paid && !issued) return 'issuance_failure';
    return 'general';
  }, [data?.ticket, data?.booking]);

  useEffect(() => {
    if (showClose && data?.ticket) {
      setCloseForm((f) => ({ ...f, refund_method: suggestedRefundMethod }));
    }
  }, [showClose, suggestedRefundMethod, data]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text) return;
    const optimistic = makeOptimisticMessage({
      ticketId: id,
      senderType: 'admin',
      senderName: adminMe?.name || 'Support',
      senderRole: adminMe?.role || 'admin',
      message: text,
    });
    setReply('');
    setData((prev) => (prev ? { ...prev, messages: [...(prev.messages || []), optimistic] } : prev));
    scrollToBottom();
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/support/tickets/${id}/reply`,
        { message: text },
        { withCredentials: true },
      );
      setData((prev) => {
        if (!prev) return prev;
        const without = (prev.messages || []).filter((m) => m.id !== optimistic.id);
        const merged = mergeThreadMessages(without, [r.data.message]);
        sinceRef.current = maxMessageCursor(merged);
        return {
          ...prev,
          messages: merged,
          ticket: { ...prev.ticket, status: 'in_process', last_message_at: r.data.message?.created_at },
        };
      });
    } catch (e) {
      setData((prev) => (
        prev ? { ...prev, messages: (prev.messages || []).filter((m) => m.id !== optimistic.id) } : prev
      ));
      setReply(text);
      alert(e.response?.data?.detail || 'Reply failed');
    }
    setSending(false);
  };

  const changeStatus = async (status) => {
    try {
      await axios.put(`${API}/admin/support/tickets/${id}/status`, { status }, { withCredentials: true });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Status change failed'); }
  };

  const applyAssign = async () => {
    try {
      await axios.put(
        `${API}/admin/support/tickets/${id}/assign`,
        { admin_id: assignValue || null },
        { withCredentials: true },
      );
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Assign failed');
    }
  };

  const submitInternalNote = async () => {
    if (!internalNote.trim()) return;
    setInternalSending(true);
    try {
      await axios.post(
        `${API}/admin/support/tickets/${id}/internal-note`,
        { message: internalNote },
        { withCredentials: true },
      );
      setInternalNote('');
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not save internal note');
    }
    setInternalSending(false);
  };

  const submitClose = async () => {
    setClosing(true);
    try {
      await axios.post(
        `${API}/admin/support/tickets/${id}/close`,
        {
          refund_amount: parseFloat(closeForm.refund_amount || 0),
          refund_reference: closeForm.refund_reference,
          refund_method: closeForm.refund_method,
          notes: closeForm.notes,
        },
        { withCredentials: true },
      );
      setShowClose(false);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Close failed');
    }
    setClosing(false);
  };

  const submitPaymentRequest = async () => {
    const amt = parseFloat(payReqForm.amount || 0);
    if (!amt || amt <= 0) { alert('Enter a positive amount'); return; }
    setPayReqSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/support/tickets/${id}/payment-request`,
        { amount: amt, notes: payReqForm.notes || '' },
        { withCredentials: true },
      );
      setShowPayReq(false);
      setPayReqForm({ amount: '', notes: '' });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not create payment request');
    }
    setPayReqSubmitting(false);
  };

  const cancelPaymentRequest = async (paymentId) => {
    if (!window.confirm('Cancel this open payment request?')) return;
    try {
      await axios.post(
        `${API}/admin/support/payment-requests/${paymentId}/cancel`,
        {},
        { withCredentials: true },
      );
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const closeRefundModal = () => {
    setShowRefund(false);
    setRefundStep('form');
  };

  const openRefundModal = () => {
    const rem = Number(data?.refund_ledger?.remaining_refundable ?? 0);
    const suggested = Number(data?.refund_breakdown?.refund_amount ?? 0);
    const amt = suggested > 0 ? Math.min(suggested, rem || suggested) : (rem > 0 ? rem : 0);
    const isFull = rem > 0 && amt >= rem - 0.01;
    setRefundForm({
      amount: amt > 0 ? String(roundRefundAmount(amt)) : '',
      reference: '',
      notes: '',
      close_ticket: isFull,
      resolution_type: suggestedResolutionType,
      resolution_remark: '',
      cancel_scope: 'full',
      selected_pax: [],
      supplier_cancel_fee: data?.refund_breakdown?.supplier_fee != null ? String(data.refund_breakdown.supplier_fee) : '',
    });
    setRefundStep('form');
    setShowRefund(true);
  };

  const setRefundPercent = (pct) => {
    if (remainingRefundable <= 0) return;
    setRefundForm((f) => ({
      ...f,
      amount: String(roundRefundAmount(remainingRefundable * pct)),
    }));
  };

  const submitRefund = async () => {
    const amt = parseFloat(refundForm.amount || 0);
    if (!amt || amt <= 0) { alert('Enter a positive refund amount'); return; }
    const remaining = Number(data?.refund_ledger?.remaining_refundable ?? 0);
    if (data?.refund_ledger && remaining <= 0) {
      alert('Nothing left to refund on this booking (ledger remaining is ₹0).');
      return;
    }
    if (data?.refund_ledger && amt > remaining + 0.01) {
      alert(`Amount exceeds remaining refundable (₹${remaining.toLocaleString('en-IN')}).`);
      return;
    }
    const keys = data?.ops_booking_keys || resolveOpsBookingKeys(data?.booking, data?.ticket?.booking_id);
    const bookId = keys?.canonical_id || keys?.canonicalId || data?.ticket?.booking_id || '';
    const isFullRefund = remaining > 0 && amt >= remaining - 0.01;
    setRefundSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/support/tickets/${id}/refund`,
        {
          amount: amt,
          reference: refundForm.reference || '',
          notes: refundForm.notes || '',
          close_ticket: Boolean(refundForm.close_ticket),
          resolution_type: refundForm.resolution_type || suggestedResolutionType,
          resolution_remark: refundForm.resolution_remark || '',
          apply_booking_status: true,
          cancel_scope: refundForm.cancel_scope || 'full',
          cancelled_passengers: (refundForm.selected_pax || []).map((idx) => {
            const paxList = data?.passengers || data?.booking?.passengers || [];
            const p = paxList[idx] || {};
            const name = [p.firstName || p.first_name, p.lastName || p.last_name].filter(Boolean).join(' ') || p.name || `Pax ${idx + 1}`;
            return { pax_index: idx, name, ticket_number: p.ticketNumber || p.ticket_number || '' };
          }),
          supplier_cancel_fee: refundForm.supplier_cancel_fee ? parseFloat(refundForm.supplier_cancel_fee) : undefined,
        },
        { withCredentials: true },
      );
      const method = r.data?.payment?.method || expectedRefundMethod;
      closeRefundModal();
      setRefundForm({
        amount: '',
        reference: '',
        notes: '',
        close_ticket: true,
        resolution_type: '',
        resolution_remark: '',
      });
      if (method === 'offline_manual') {
        setRefundToast({
          kind: 'pg',
          msg: `Refund of ₹${amt.toLocaleString('en-IN')} queued for gateway processing.`,
          href: `/admin/refunds?search=${encodeURIComponent(bookId)}`,
        });
      } else {
        const label = refundRouting?.refund_to_label || method;
        const closed = Boolean(refundForm.close_ticket);
        setRefundToast({
          kind: 'ok',
          msg: closed
            ? `Refund of ₹${amt.toLocaleString('en-IN')} processed via ${label}. Ticket closed.`
            : `Refund of ₹${amt.toLocaleString('en-IN')} processed via ${label}.`,
        });
      }
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Refund failed');
    }
    setRefundSubmitting(false);
  };

  // GST documents (Phase 2D.2) ---------------------------------------------
  const paxCount = useMemo(() => {
    const pax = data?.booking?.passengers?.length
      || data?.ticket?.booking_snapshot?.passengers_count
      || 1;
    return Math.max(1, Number(pax) || 1);
  }, [data]);

  const openCnModal = () => {
    setCnForm({
      cancel_fee_per_pax: '',
      pax_count: String(paxCount),
      reason: data?.ticket?.subject || 'Booking cancellation',
      airline_refund: '',
    });
    setShowCnModal(true);
  };

  const openSuppModal = () => {
    const category = data?.ticket?.category;
    const parentType = category === 'date_change' ? 'date_change'
                     : category === 'name_correction' ? 'name_change'
                     : category === 'seat_selection' || category === 'add_baggage' || category === 'add_meal' ? 'ssr'
                     : 'reschedule';
    setSuppForm({
      service_fee_per_pax: '',
      pax_count: String(paxCount),
      description: data?.ticket?.subject || '',
      parent_type: parentType,
      airline_charge: '',
    });
    setShowSuppModal(true);
  };

  const cnPreview = useMemo(() => {
    const fee = parseFloat(cnForm.cancel_fee_per_pax || 0);
    const pax = parseInt(cnForm.pax_count || 0, 10);
    const retained = fee * pax;
    const originalMarkup = data?.booking?.markup_applied?.amount || 0;
    const refundedMarkup = Math.max(0, originalMarkup - retained);
    const state = data?.booking?.agent_state_code;
    // Intra-state if both are MH (27) — matches backend default
    const companyState = '27';
    const gstRate = 18;
    const gst = refundedMarkup * gstRate / 100;
    const overflow = retained > originalMarkup + 0.01;
    return {
      retained, refundedMarkup,
      gst: parseFloat(gst.toFixed(2)),
      total: parseFloat((refundedMarkup + gst).toFixed(2)),
      originalMarkup,
      overflow,
    };
  }, [cnForm, data]);

  const suppPreview = useMemo(() => {
    const fee = parseFloat(suppForm.service_fee_per_pax || 0);
    const pax = parseInt(suppForm.pax_count || 0, 10);
    const airline = parseFloat(suppForm.airline_charge || 0);
    const taxable = fee * pax;
    const gst = taxable * 18 / 100;
    return {
      taxable,
      gst: parseFloat(gst.toFixed(2)),
      airline,
      total: parseFloat((airline + taxable + gst).toFixed(2)),
    };
  }, [suppForm]);

  const submitCreditNote = async () => {
    const fee = parseFloat(cnForm.cancel_fee_per_pax || 0);
    const pax = parseInt(cnForm.pax_count || 0, 10);
    if (fee < 0 || pax < 1) { alert('Enter a valid cancel fee and passenger count'); return; }
    if (cnPreview.overflow) {
      alert('Retained cancel fee exceeds original markup. Please lower the fee or generate a supplementary invoice for the excess first.');
      return;
    }
    const originalInvoiceId = data?.booking?.gst_invoice_id;
    if (!originalInvoiceId) {
      alert('No original tax invoice found on this booking. Credit note cannot be issued.');
      return;
    }
    setCnSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/invoices/${originalInvoiceId}/credit-note`,
        {
          cancel_fee_per_pax: fee,
          pax_count: pax,
          reason: cnForm.reason || '',
          airline_refund: parseFloat(cnForm.airline_refund || 0),
        },
        { withCredentials: true },
      );
      setShowCnModal(false);
      await load();
      alert('Credit note generated successfully.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate credit note');
    }
    setCnSubmitting(false);
  };

  const submitSupplementary = async () => {
    const fee = parseFloat(suppForm.service_fee_per_pax || 0);
    const pax = parseInt(suppForm.pax_count || 0, 10);
    if (fee <= 0 || pax < 1) { alert('Enter a valid service fee and passenger count'); return; }
    const bookingId = data?.ticket?.booking_id;
    if (!bookingId) { alert('No booking linked to this ticket'); return; }
    setSuppSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/bookings/${bookingId}/supplementary-invoice`,
        {
          service_fee_per_pax: fee,
          pax_count: pax,
          description: suppForm.description || '',
          parent_type: suppForm.parent_type || 'reschedule',
          airline_charge: parseFloat(suppForm.airline_charge || 0),
        },
        { withCredentials: true },
      );
      setShowSuppModal(false);
      await load();
      alert('Supplementary invoice generated successfully.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate supplementary invoice');
    }
    setSuppSubmitting(false);
  };

  const downloadInvoice = async (invoiceId, label) => {
    try {
      const r = await axios.get(`${API}/admin/invoices/${invoiceId}/download`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed'); }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
  if (detailError) return <div className="p-12 text-center text-red-600 text-sm">{detailError}</div>;
  if (!data?.ticket) return <div className="p-12 text-center text-gray-500">Ticket not found.</div>;

  const opsKeys = data.ops_booking_keys || resolveOpsBookingKeys(data.booking, t.booking_id);
  const canonicalBookingId = opsKeys.canonical_id || opsKeys.canonicalId || t.booking_id;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <button onClick={() => navigate('/admin/support')} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to queue
      </button>

      {refundToast && (
        <div
          className={`mb-4 flex flex-wrap items-center gap-2 text-sm px-4 py-3 rounded-xl border ${
            refundToast.kind === 'pg'
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-emerald-50 border-emerald-200 text-emerald-900'
          }`}
          data-testid="refund-success-toast"
        >
          {refundToast.kind === 'pg' ? (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="flex-1 min-w-0">{refundToast.msg}</span>
          {refundToast.href && (
            <a
              href={refundToast.href}
              className="text-xs font-semibold underline whitespace-nowrap"
              data-testid="refund-toast-tracker-link"
            >
              Open PG Refund Tracker
            </a>
          )}
          <button
            type="button"
            onClick={() => setRefundToast(null)}
            className="p-1 rounded hover:bg-black/5"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 opacity-60" />
          </button>
        </div>
      )}

      {/* Booking summary (rich, shared component) */}
      <BookingSummaryCard booking={data.booking} snap={t.booking_snapshot} bookingId={t.booking_id} isAdmin={true} />

      <AdminTicketBookingOpsPanel
        bookingOps={data.booking_ops}
        opsBookingKeys={data.ops_booking_keys}
        refundTracker={data.refund_tracker}
        bookingMonitor={data.booking_monitor}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        <a
          href={`/admin/ops-monitor?focus=${encodeURIComponent(canonicalBookingId)}`}
          className="text-xs px-3 py-1.5 rounded-lg border border-[#1B375B] text-[#1B375B] font-semibold hover:bg-[#1B375B]/5"
        >
          Open in Live Monitor
        </a>
        <a
          href={`/admin/refunds?search=${encodeURIComponent(canonicalBookingId)}`}
          className="text-xs px-3 py-1.5 rounded-lg border border-emerald-700 text-emerald-800 font-semibold hover:bg-emerald-50"
        >
          PG Refund Tracker
        </a>
        {data.refund_tracker?.refund_status && (
          <span className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-800 font-medium">
            PG refund: {data.refund_tracker.refund_status.replace('_', ' ')}
          </span>
        )}
        {data.booking_monitor?.sla_breached && (
          <span className="text-xs px-3 py-1.5 rounded-full bg-rose-100 text-rose-800 font-bold">
            Monitor SLA breached
          </span>
        )}
      </div>

      {(data.refund_ledger || data.booking_monitor) && (
        <div className="grid md:grid-cols-2 gap-3 mb-4 text-sm">
          {data.refund_ledger && (
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Refund ledger</p>
              <p>Cap: ₹{Number(data.refund_ledger.cap || 0).toLocaleString('en-IN')}</p>
              <p>Remaining: ₹{Number(data.refund_ledger.remaining_refundable || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-gray-500 mt-1">
                Cap is the maximum refundable for this booking. Remaining is what support can still refund without exceeding the cap.
              </p>
              {Number(data.refund_ledger.total_refunded || 0) > 0 && (
                <p className="text-xs text-gray-700 mt-2">
                  Already refunded: ₹{Number(data.refund_ledger.total_refunded).toLocaleString('en-IN')}
                  {' '}(support payments: ₹{Number(data.refund_ledger.ticket_payments_refunded || 0).toLocaleString('en-IN')}
                  , PG/cancellation: ₹{Number(data.refund_ledger.cancellation_completed || 0).toLocaleString('en-IN')})
                </p>
              )}
              {data.refund_tracker?.refund_status === 'pending_gateway' && (
                <p className="text-amber-700 text-xs mt-2">
                  Gateway refund queued — accounts must mark processed in Refund Tracker.
                </p>
              )}
            </div>
          )}
          {data.booking_monitor && (
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Live monitor</p>
              <p>Status: {data.booking_monitor.main_status || '—'}</p>
              <p className="text-xs text-gray-600">{data.booking_monitor.sla_label || ''}</p>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-3">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500">Ticket Reference</div>
            <div className="font-mono font-semibold text-blue-600">{t.reference}</div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
              <Pill label="Category" value={CATEGORIES[t.category]} />
              <Pill label="Priority" value={t.priority} valueClass={PRIORITY_COLORS[t.priority]} />
              <Pill label="Status" value={<StatusBadge status={t.status} />} />
              <Pill label="Created" value={new Date(t.created_at).toLocaleDateString()} />
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs">
              <div className="text-gray-500">Raised by</div>
              <div className="font-medium">{t.created_by_name}</div>
              <div className="text-gray-600">{t.email}</div>
              <div className="text-gray-600">{t.phone}</div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <div className="text-xs font-medium text-gray-700">Assignment</div>
            {canAssignTickets ? (
              <>
                <select
                  value={assignValue}
                  onChange={(e) => setAssignValue(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5"
                  data-testid="assign-select"
                >
                  <option value="">Unassigned</option>
                  {assignees.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name || a.email || a.id} (Ops Executive)
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={applyAssign}
                  className="w-full text-sm py-2 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium"
                  data-testid="assign-save-btn"
                >
                  Save assignment
                </button>
              </>
            ) : canSelfClaim ? (
              <div data-testid="assign-claim-block">
                <p className="text-sm text-gray-700 mb-2">This ticket is unassigned.</p>
                <button
                  type="button"
                  onClick={claimTicket}
                  className="w-full text-sm py-2 rounded-lg bg-[#1B375B] text-white font-semibold hover:bg-[#0f2742]"
                  data-testid="assign-claim-btn"
                >
                  Claim ticket
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-800" data-testid="assign-readonly">
                {t.assigned_to_name || 'Unassigned'}
                {isAssignedToMe ? (
                  <span className="block text-[11px] text-emerald-700 mt-1 font-medium">Assigned to you</span>
                ) : (
                  <span className="block text-[11px] text-gray-500 mt-1">
                    {isOpsExecutive
                      ? 'Assigned to another team member. Ask Ops Manager to reassign if needed.'
                      : 'Only Ops Manager or Super Admin can reassign tickets.'}
                  </span>
                )}
              </p>
            )}
            {t.first_admin_reply_at && (
              <div className="text-[11px] text-gray-600 pt-2 border-t border-gray-100">
                First support reply:<br />
                <span className="font-medium text-gray-800">{new Date(t.first_admin_reply_at).toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          {t.status !== 'closed' && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
              <div className="text-xs font-medium text-gray-700">Actions</div>
              <select
                data-testid="status-select"
                value={t.status}
                onChange={(e) => changeStatus(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5"
              >
                <option value="unread">Unread</option>
                <option value="open">Open</option>
                <option value="in_process">In Process</option>
                <option value="need_reply">Need Reply</option>
              </select>
              <button
                data-testid="request-payment-btn"
                onClick={() => setShowPayReq(true)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> Request Payment
              </button>
              {canProcessRefund ? (
                <button
                  data-testid="process-refund-btn"
                  onClick={openRefundModal}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Undo2 className="w-4 h-4" /> Process Refund
                </button>
              ) : (
                <p
                  className="text-[11px] text-gray-500 text-center px-2 py-1"
                  data-testid="process-refund-denied"
                >
                  Refunds: Ops Manager, Super Admin, or Accounts only.
                </p>
              )}
              <button
                data-testid="close-ticket-btn"
                onClick={() => setShowClose(true)}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-medium py-2 rounded-lg hover:opacity-90"
              >
                Close Ticket
              </button>
            </div>
          )}

          {/* GST Documents panel (Phase 2D.2) — agent bookings only */}
          {isAgent && (data?.booking?.gst_invoice_id || ['cancel_refund', 'date_change', 'name_correction', 'seat_selection', 'add_baggage', 'add_meal', 'special_request'].includes(t.category)) && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2" data-testid="gst-docs-panel">
              <div className="text-xs font-medium text-gray-700 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5 text-indigo-600" /> GST Documents
              </div>

              {/* Original Tax Invoice */}
              {data?.booking?.gst_invoice_id && (
                <button
                  onClick={() => downloadInvoice(data.booking.gst_invoice_id,
                    `tax_invoice_${(data.booking.gst_invoice_number || '').replace(/\//g, '_')}`)}
                  className="w-full flex items-center justify-between text-xs px-2 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 rounded-lg"
                  data-testid="download-tax-invoice"
                  title={data.booking.gst_invoice_number}
                >
                  <span className="truncate">Tax Invoice · {data.booking.gst_invoice_number}</span>
                  <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                </button>
              )}

              {/* Credit Note — if already issued, show download; else show Generate for cancel_refund */}
              {data?.booking?.credit_note_id ? (
                <button
                  onClick={() => downloadInvoice(data.booking.credit_note_id,
                    `credit_note_${(data.booking.credit_note_number || '').replace(/\//g, '_')}`)}
                  className="w-full flex items-center justify-between text-xs px-2 py-1.5 bg-red-50 hover:bg-red-100 text-red-900 rounded-lg"
                  data-testid="download-credit-note"
                  title={data.booking.credit_note_number}
                >
                  <span className="truncate">Credit Note · {data.booking.credit_note_number}</span>
                  <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                </button>
              ) : (
                t.category === 'cancel_refund' && data?.booking?.gst_invoice_id && t.status !== 'closed' && (
                  <p className="text-[10px] text-red-900 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5" data-testid="auto-cn-note">
                    Credit note is generated automatically when you process the refund in Support.
                  </p>
                )
              )}

              {/* Supplementary invoices already issued */}
              {(data?.booking?.supplementary_invoices || []).map((s, i) => (
                <button
                  key={s.id || i}
                  onClick={() => downloadInvoice(s.id, `supp_${(s.number || '').replace(/\//g, '_')}`)}
                  className="w-full flex items-center justify-between text-xs px-2 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-lg"
                  data-testid={`download-supp-${i}`}
                  title={`${s.number} · ${s.parent_type}`}
                >
                  <span className="truncate">Supp · {s.number}</span>
                  <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                </button>
              ))}

              {/* Generate supplementary — for reschedule / name / SSR categories */}
              {t.status !== 'closed' && ['date_change', 'name_correction', 'seat_selection',
                                          'add_baggage', 'add_meal', 'special_request'].includes(t.category) && (
                <button
                  onClick={openSuppModal}
                  className="w-full flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium"
                  data-testid="generate-supp-btn"
                >
                  <FilePlus className="w-3.5 h-3.5" /> Generate Supplementary Invoice
                </button>
              )}

              {!data?.booking?.gst_invoice_id && (
                <div className="text-[11px] text-gray-500 italic mt-1">
                  Original tax invoice missing — GST doc generation requires a B2B agent with valid GSTIN on the booking.
                  {data?.booking?.gst_invoice_deferred_reason && (
                    <span className="block text-amber-700 not-italic mt-0.5" data-testid="support-gst-deferred-reason">
                      Deferred: {data.booking.gst_invoice_deferred_reason.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Payments */}
          <PaymentsPanel
            payments={data.payments || []}
            onCancel={cancelPaymentRequest}
            isAdmin
          />

          {/* Refund info (if closed) */}
          {t.status === 'closed' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
              <div className="font-semibold text-emerald-800 flex items-center gap-1 mb-1"><CheckCircle2 className="w-4 h-4" /> Closed</div>
              <div className="text-xs text-gray-700">
                {t.refund_amount ? (
                  <>Refund <strong>₹{t.refund_amount.toLocaleString()}</strong> via <em>{t.refund_method}</em><br/>Ref: {t.refund_reference || '—'}</>
                ) : 'No refund recorded.'}
              </div>
            </div>
          )}

          {/* Related */}
          {data.related?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-medium text-gray-700 mb-2">Related Tickets</div>
              {data.related.map((r) => (
                <button key={r.id} onClick={() => navigate(`/admin/support/${r.id}`)}
                        className="block w-full text-left text-xs py-1 hover:text-blue-600 font-mono">
                  {r.reference} <span className="text-gray-500">· {r.status}</span>
                </button>
              ))}
            </div>
          )}

          {t.status !== 'closed' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
              <div className="text-xs font-medium text-amber-900">Internal note</div>
              <p className="text-[10px] text-amber-800/90">Visible to admins only — not sent to the customer.</p>
              <textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                rows={3}
                placeholder="Ops notes, supplier refs, coordination…"
                className="w-full text-xs border border-amber-200 rounded-lg px-2 py-1.5 resize-none bg-white"
                data-testid="internal-note-textarea"
              />
              <button
                type="button"
                onClick={submitInternalNote}
                disabled={internalSending || !internalNote.trim()}
                className="w-full text-xs py-2 rounded-lg bg-amber-700 text-white font-medium disabled:opacity-50"
                data-testid="internal-note-save"
              >
                {internalSending ? 'Saving…' : 'Save internal note'}
              </button>
            </div>
          )}

          {(data.audit_events || []).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 max-h-56 overflow-y-auto">
              <div className="text-xs font-medium text-gray-700 mb-2">Activity log</div>
              <ul className="space-y-2 text-[11px] text-gray-600">
                {(data.audit_events || []).map((ev) => (
                  <li key={ev.id} className="border-b border-gray-50 pb-2 last:border-0">
                    <div className="font-medium text-gray-800">{ev.action}</div>
                    <div className="text-gray-500">{new Date(ev.created_at).toLocaleString()} · {ev.actor_name || ev.actor_id}</div>
                    {ev.payload && Object.keys(ev.payload).length > 0 && (
                      <pre className="mt-1 text-[10px] bg-gray-50 rounded p-1 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(ev.payload)}</pre>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Thread */}
        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 font-semibold text-gray-900">{t.subject}</div>
          <div
            ref={threadRef}
            onScroll={onThreadScroll}
            className="p-4 space-y-4 max-h-[520px] overflow-y-auto"
          >
            {data.messages?.map((m) => (
              <MessageBubble key={m.id} msg={m} ticketRef={id} />
            ))}
          </div>
          {t.status !== 'closed' && (
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-2">
              <textarea
                data-testid="reply-textarea"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Write your reply to the user…"
                rows={2}
                className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 resize-none"
              />
              <button
                data-testid="send-reply-btn"
                onClick={sendReply}
                disabled={sending || !reply.trim()}
                className="self-end bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Close dialog */}
      {showClose && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowClose(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" data-testid="close-dialog">
            <h3 className="font-semibold text-gray-900">Close Ticket {isAgent && '& Process Refund'}</h3>
            {isAgent && (
              <div className="text-xs bg-blue-50 text-blue-800 p-2 rounded">
                Agent booking (<strong>{paymentMode}</strong>). Suggested refund method: <strong>{suggestedRefundMethod.replace('_', ' ')}</strong>.
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Refund Amount (₹)</label>
              <input type="number" min="0" step="0.01"
                     data-testid="refund-amount-input"
                     value={closeForm.refund_amount}
                     onChange={(e) => setCloseForm({ ...closeForm, refund_amount: e.target.value })}
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Refund Reference (bank/txn/PNR)</label>
              <input type="text"
                     data-testid="refund-reference-input"
                     value={closeForm.refund_reference}
                     onChange={(e) => setCloseForm({ ...closeForm, refund_reference: e.target.value })}
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Method</label>
              <select value={closeForm.refund_method}
                      data-testid="refund-method-select"
                      onChange={(e) => setCloseForm({ ...closeForm, refund_method: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="none">No Refund (close only)</option>
                {isAgent && <option value="wallet_credit">Cash Wallet</option>}
                {isAgent && <option value="credit_ledger">Credit Ledger (reduce credit used)</option>}
                <option value="offline_manual">Offline / Manual (no auto-credit)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea rows={2}
                        data-testid="refund-notes-input"
                        value={closeForm.notes}
                        onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowClose(false)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm">Cancel</button>
              <button
                onClick={submitClose}
                disabled={closing}
                data-testid="confirm-close-btn"
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {closing && <Loader2 className="w-4 h-4 animate-spin" />} Confirm Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Request Payment dialog */}
      {showPayReq && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPayReq(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" data-testid="payreq-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Request Additional Payment</h3>
              <button onClick={() => setShowPayReq(false)}><X className="w-4 h-4 text-gray-500"/></button>
            </div>
            <div className="text-xs bg-blue-50 text-blue-800 p-2 rounded">
              User will see this as a "Pending payment" on their ticket.
              {isAgent && paymentMode && <> Allowed methods depend on booking mode <strong>{paymentMode}</strong>.</>}
              {!isAgent && <> B2C can only pay via Payment Gateway.</>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Amount (₹)</label>
              <input type="number" min="0" step="0.01"
                     data-testid="payreq-amount-input"
                     value={payReqForm.amount}
                     onChange={(e) => setPayReqForm({ ...payReqForm, amount: e.target.value })}
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes (reason for the user)</label>
              <textarea rows={3}
                        data-testid="payreq-notes-input"
                        value={payReqForm.notes}
                        onChange={(e) => setPayReqForm({ ...payReqForm, notes: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowPayReq(false)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm">Cancel</button>
              <button
                onClick={submitPaymentRequest}
                disabled={payReqSubmitting}
                data-testid="confirm-payreq-btn"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {payReqSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} Send Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund dialog */}
      {showRefund && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeRefundModal}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="refund-dialog"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-900">Process Refund</h3>
                <p className="text-[11px] text-gray-500 mt-0.5" data-testid="refund-step-label">
                  {refundStep === 'form' ? 'Step 1 of 2 — Details' : 'Step 2 of 2 — Confirm'}
                </p>
              </div>
              <button type="button" onClick={closeRefundModal} aria-label="Close">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {refundStep === 'form' && (
              <p className="text-[11px] text-gray-600 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid="refund-workflow-note">
                Process the refund here. Check <strong>Close ticket after refund</strong> when the case is fully resolved
                (recommended for full refunds). Partial refunds can stay open for follow-up.
              </p>
            )}

            {pgRefundPending && refundStep === 'form' && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="refund-pg-pending-warning">
                A gateway refund may already be in flight for this booking (
                <strong>pending gateway</strong>
                ). Check{' '}
                <a
                  href={`/admin/refunds?search=${encodeURIComponent(canonicalBookingId)}`}
                  className="font-semibold underline"
                >
                  PG Refund Tracker
                </a>
                {' '}before issuing another PG refund.
              </p>
            )}

            {refundStep === 'review' && (
              <div className="space-y-3" data-testid="refund-review-step">
                <RefundRoutingPanel routing={refundRouting} />
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2 text-sm">
                  <p className="text-[10px] uppercase font-bold text-emerald-800">Confirm refund</p>
                  <div className="flex justify-between gap-2"><span className="text-gray-600">Booking</span><span className="font-mono font-medium">{canonicalBookingId}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-600">Amount</span><span className="font-bold text-emerald-800">₹{refundAmountNum.toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-600">Paid with</span><span className="font-medium">{refundRouting?.paid_with_label}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-600">Refund to</span><span className="font-medium">{refundRouting?.refund_to_label}</span></div>
                  {refundForm.reference ? (
                    <div className="flex justify-between gap-2"><span className="text-gray-600">Reference</span><span className="font-mono text-xs truncate max-w-[60%] text-right">{refundForm.reference}</span></div>
                  ) : null}
                  {refundForm.notes ? (
                    <div><span className="text-gray-600 block mb-0.5">Notes</span><span>{refundForm.notes}</span></div>
                  ) : null}
                  <div className="flex justify-between gap-2 pt-1 border-t border-emerald-200">
                    <span className="text-gray-600">Close ticket</span>
                    <span className="font-medium">{refundForm.close_ticket ? 'Yes' : 'No'}</span>
                  </div>
                  {refundForm.resolution_type ? (
                    <div><span className="text-gray-600 block mb-0.5">Resolution</span><span className="capitalize">{refundForm.resolution_type.replace(/_/g, ' ')}</span></div>
                  ) : null}
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setRefundStep('form')} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm" data-testid="refund-review-back-btn">Back</button>
                  <button type="button" onClick={submitRefund} disabled={refundSubmitting} data-testid="confirm-refund-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                    {refundSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} Confirm & process
                  </button>
                </div>
              </div>
            )}

            {refundStep === 'form' && (
              <>
                <RefundRoutingPanel routing={refundRouting} />
                {data.refund_ledger ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-1" data-testid="refund-ledger-modal">
                    <p className="text-[10px] uppercase font-bold text-gray-500">Refund ledger</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-gray-500 text-xs">Cap</span><p className="font-semibold">₹{refundCap.toLocaleString('en-IN')}</p></div>
                      <div><span className="text-gray-500 text-xs">Remaining</span><p className="font-semibold text-emerald-700">₹{remainingRefundable.toLocaleString('en-IN')}</p></div>
                    </div>
                    {Number(data.refund_ledger.total_refunded || 0) > 0 && (
                      <p className="text-xs text-gray-600 pt-1">Already refunded: ₹{Number(data.refund_ledger.total_refunded).toLocaleString('en-IN')}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">No ledger loaded — amount is still validated on submit.</p>
                )}
                {data?.refund_breakdown && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1" data-testid="refund-breakdown-panel">
                    <p className="text-[10px] uppercase font-bold text-gray-500">Refund breakdown</p>
                    <div className="flex justify-between"><span>Gross paid</span><span>₹{Number(data.refund_breakdown.gross_paid || 0).toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Supplier fee</span><span>₹{Number(data.refund_breakdown.supplier_fee || 0).toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-amber-900 font-medium"><span>Service fee (not refunded)</span><span>₹{Number(data.refund_breakdown.service_fee_retained || 0).toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between font-semibold text-emerald-800 border-t border-slate-200 pt-1"><span>Suggested refund</span><span>₹{Number(data.refund_breakdown.refund_amount || 0).toLocaleString('en-IN')}</span></div>
                  </div>
                )}
                {priorRefunds.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid="refund-prior-list">
                    <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Prior refunds on ticket</p>
                    <div className="space-y-2">
                      {priorRefunds.map((pr) => (
                        <div key={pr.id} className="flex justify-between gap-2 text-xs border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                          <div>
                            <span className="font-semibold">₹{Number(pr.amount).toLocaleString('en-IN')}</span>
                            {pr.method && <span className="text-gray-500 ml-1">· {REFUND_PAYMENT_METHOD_LABELS[pr.method] || pr.method}</span>}
                            {pr.reference && <span className="block font-mono text-[10px] text-gray-500">Ref: {pr.reference}</span>}
                          </div>
                          <span className="text-gray-400 shrink-0">{pr.created_at ? new Date(pr.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {refundNothingLeft && (
                  <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="refund-nothing-left">Nothing left to refund (remaining ₹0).</p>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Refund amount (₹)</label>
                  {data.refund_ledger && remainingRefundable > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="refund-pct-chips">
                      {[{ pct: 0.25, label: '25%' }, { pct: 0.5, label: '50%' }, { pct: 1, label: '100%' }].map(({ pct, label }) => (
                        <button key={label} type="button" data-testid={`refund-pct-${label}`} onClick={() => setRefundPercent(pct)} className="text-[11px] font-medium px-2.5 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50">{label} remaining</button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="number" min="0" max={data.refund_ledger ? remainingRefundable : undefined} step="0.01" data-testid="refund-amount-modal-input" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} className={`flex-1 border rounded-lg px-3 py-2 text-sm ${refundOverRemaining ? 'border-rose-400 bg-rose-50' : 'border-gray-300'}`} />
                    {data.refund_ledger && remainingRefundable > 0 && (
                      <button type="button" data-testid="refund-fill-remaining-btn" onClick={() => setRefundForm((f) => ({ ...f, amount: String(remainingRefundable) }))} className="shrink-0 text-xs font-medium px-3 py-2 border border-emerald-300 text-emerald-800 rounded-lg hover:bg-emerald-50">Fill remaining</button>
                    )}
                  </div>
                  {refundOverRemaining && <p className="text-xs text-rose-600 mt-1">Exceeds remaining ₹{remainingRefundable.toLocaleString('en-IN')}.</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Internal reference (NEFT UTR / bank ref / PNR)</label>
                  <input type="text" data-testid="refund-reference-modal-input" value={refundForm.reference} onChange={(e) => setRefundForm({ ...refundForm, reference: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Optional support record" />
                  {expectedRefundMethod === 'offline_manual' && (
                    <p className="text-[11px] text-amber-800 mt-1">PG refund reference is stamped later in <a href={`/admin/refunds?search=${encodeURIComponent(canonicalBookingId)}`} className="font-semibold underline">PG Refund Tracker</a> after accounts processes the gateway payout.</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Resolution type</label>
                  <select
                    data-testid="refund-resolution-type-select"
                    value={refundForm.resolution_type || suggestedResolutionType}
                    onChange={(e) => setRefundForm({ ...refundForm, resolution_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="issuance_failure">Issuance failure — full refund</option>
                    <option value="post_ticket_cancel">Post-ticket cancellation — refund</option>
                    <option value="general">Other / partial</option>
                  </select>
                </div>
                {(data?.passengers?.length || data?.booking?.passengers?.length) > 1 && (
                  <div className="space-y-2" data-testid="refund-pax-select">
                    <label className="block text-xs font-medium text-gray-700">Passengers to cancel (partial)</label>
                    <p className="text-[10px] text-gray-500">Select passengers for partial cancel; credit note uses selected count.</p>
                    <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                      {(data.passengers || data.booking?.passengers || []).map((p, idx) => {
                        const name = [p.firstName || p.first_name, p.lastName || p.last_name].filter(Boolean).join(' ') || p.name || `Passenger ${idx + 1}`;
                        const checked = (refundForm.selected_pax || []).includes(idx);
                        return (
                          <label key={idx} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setRefundForm((f) => {
                                  const cur = new Set(f.selected_pax || []);
                                  if (cur.has(idx)) cur.delete(idx);
                                  else cur.add(idx);
                                  const next = [...cur].sort((a, b) => a - b);
                                  return {
                                    ...f,
                                    selected_pax: next,
                                    cancel_scope: next.length && next.length < (data.passengers || data.booking?.passengers || []).length
                                      ? 'partial_passengers'
                                      : 'full',
                                  };
                                });
                              }}
                            />
                            <span>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="refund-close-ticket-checkbox">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={Boolean(refundForm.close_ticket)}
                    onChange={(e) => setRefundForm({ ...refundForm, close_ticket: e.target.checked })}
                  />
                  <span>Close ticket after this refund</span>
                </label>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Closure remark (optional)</label>
                  <textarea
                    rows={2}
                    data-testid="refund-resolution-remark-input"
                    value={refundForm.resolution_remark}
                    onChange={(e) => setRefundForm({ ...refundForm, resolution_remark: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                    placeholder="Shown when ticket is closed"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                  <textarea rows={2} data-testid="refund-notes-modal-input" value={refundForm.notes} onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={closeRefundModal} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm">Cancel</button>
                  <button type="button" onClick={() => setRefundStep('review')} disabled={refundFormInvalid} data-testid="refund-review-next-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50">Review refund →</button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

      {/* Credit Note dialog (Phase 2D.2) */}
      {showCnModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCnModal(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-3" data-testid="cn-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FilePlus className="w-5 h-5 text-red-600" /> Generate Credit Note
              </h3>
              <button onClick={() => setShowCnModal(false)}><X className="w-4 h-4 text-gray-500"/></button>
            </div>
            <div className="text-xs bg-red-50 text-red-900 p-2 rounded">
              Reverses GST on the refunded portion of the original markup.
              Retained cancel fee stays taxed under the original invoice (no double GST).
              Original Tax Invoice: <strong>{data?.booking?.gst_invoice_number}</strong>
              {cnPreview.originalMarkup > 0 && (
                <> · Original markup: <strong>₹{cnPreview.originalMarkup.toLocaleString('en-IN')}</strong></>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cancel Fee per Passenger (₹)</label>
                <input type="number" min="0" step="0.01"
                       data-testid="cn-fee-input"
                       value={cnForm.cancel_fee_per_pax}
                       onChange={(e) => setCnForm({ ...cnForm, cancel_fee_per_pax: e.target.value })}
                       className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Passenger Count</label>
                <input type="number" min="1" step="1"
                       data-testid="cn-pax-input"
                       value={cnForm.pax_count}
                       onChange={(e) => setCnForm({ ...cnForm, pax_count: e.target.value })}
                       className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
              <textarea rows={2}
                        data-testid="cn-reason-input"
                        value={cnForm.reason}
                        onChange={(e) => setCnForm({ ...cnForm, reason: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Airline Refund Received (optional, ₹)</label>
              <input type="number" min="0" step="0.01"
                     data-testid="cn-airline-refund"
                     value={cnForm.airline_refund}
                     onChange={(e) => setCnForm({ ...cnForm, airline_refund: e.target.value })}
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            {/* Live preview */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs grid grid-cols-2 gap-y-1.5" data-testid="cn-preview">
              <span className="text-gray-600">Retained cancel fee</span>
              <span className="text-right font-semibold">₹{cnPreview.retained.toLocaleString('en-IN')}</span>
              <span className="text-gray-600">Refunded markup</span>
              <span className="text-right font-semibold">₹{cnPreview.refundedMarkup.toLocaleString('en-IN')}</span>
              <span className="text-gray-600">GST reversed (18%)</span>
              <span className="text-right font-semibold">₹{cnPreview.gst.toLocaleString('en-IN')}</span>
              <span className="text-gray-900 font-semibold border-t border-gray-300 pt-1.5 mt-1">Total Credit Note</span>
              <span className="text-right font-bold border-t border-gray-300 pt-1.5 mt-1">₹{cnPreview.total.toLocaleString('en-IN')}</span>
            </div>
            {cnPreview.overflow && (
              <div className="text-xs bg-amber-50 text-amber-900 p-2 rounded border border-amber-200">
                ⚠ Cancel fee exceeds original markup (₹{cnPreview.originalMarkup.toLocaleString('en-IN')}).
                Reduce the per-passenger fee, or generate a supplementary invoice for the excess first.
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowCnModal(false)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm">Cancel</button>
              <button
                onClick={submitCreditNote}
                disabled={cnSubmitting || cnPreview.overflow}
                data-testid="confirm-cn-btn"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {cnSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} Generate Credit Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supplementary Invoice dialog (Phase 2D.2) */}
      {showSuppModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSuppModal(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-3" data-testid="supp-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FilePlus className="w-5 h-5 text-amber-600" /> Generate Supplementary Invoice
              </h3>
              <button onClick={() => setShowSuppModal(false)}><X className="w-4 h-4 text-gray-500"/></button>
            </div>
            <div className="text-xs bg-amber-50 text-amber-900 p-2 rounded">
              Fresh tax invoice for a post-booking service (reschedule / name change / SSR handling).
              18% GST charged only on ACF's service fee; airline charge is pass-through.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Service Type</label>
                <select
                  data-testid="supp-type"
                  value={suppForm.parent_type}
                  onChange={(e) => setSuppForm({ ...suppForm, parent_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="date_change">Date Change</option>
                  <option value="reschedule">Reschedule</option>
                  <option value="name_change">Name Change</option>
                  <option value="ssr">SSR Handling (seat/baggage/meal)</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Passenger Count</label>
                <input type="number" min="1" step="1"
                       data-testid="supp-pax-input"
                       value={suppForm.pax_count}
                       onChange={(e) => setSuppForm({ ...suppForm, pax_count: e.target.value })}
                       className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Service Fee per Passenger (₹)</label>
                <input type="number" min="0" step="0.01"
                       data-testid="supp-fee-input"
                       value={suppForm.service_fee_per_pax}
                       onChange={(e) => setSuppForm({ ...suppForm, service_fee_per_pax: e.target.value })}
                       className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Airline Charge (pass-through, ₹)</label>
                <input type="number" min="0" step="0.01"
                       data-testid="supp-airline-input"
                       value={suppForm.airline_charge}
                       onChange={(e) => setSuppForm({ ...suppForm, airline_charge: e.target.value })}
                       className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea rows={2}
                        data-testid="supp-description-input"
                        value={suppForm.description}
                        onChange={(e) => setSuppForm({ ...suppForm, description: e.target.value })}
                        placeholder="e.g. Date changed from 10-Jun to 20-Jun"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            {/* Live preview */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs grid grid-cols-2 gap-y-1.5" data-testid="supp-preview">
              <span className="text-gray-600">ACF Service Fee (taxable)</span>
              <span className="text-right font-semibold">₹{suppPreview.taxable.toLocaleString('en-IN')}</span>
              <span className="text-gray-600">GST @ 18%</span>
              <span className="text-right font-semibold">₹{suppPreview.gst.toLocaleString('en-IN')}</span>
              <span className="text-gray-600">Airline pass-through</span>
              <span className="text-right font-semibold">₹{suppPreview.airline.toLocaleString('en-IN')}</span>
              <span className="text-gray-900 font-semibold border-t border-gray-300 pt-1.5 mt-1">Total Invoice Value</span>
              <span className="text-right font-bold border-t border-gray-300 pt-1.5 mt-1">₹{suppPreview.total.toLocaleString('en-IN')}</span>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowSuppModal(false)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm">Cancel</button>
              <button
                onClick={submitSupplementary}
                disabled={suppSubmitting}
                data-testid="confirm-supp-btn"
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {suppSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} Generate Supplementary
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const Pill = ({ label, value, valueClass = '' }) => (
  <div>
    <div className="text-gray-500">{label}</div>
    <div className={`font-medium capitalize ${valueClass}`}>{value}</div>
  </div>
);

const MessageBubble = ({ msg, ticketRef }) => {
  const isInternal = msg.sender_type === 'internal';
  const isAdmin = msg.sender_type === 'admin';
  const pending = Boolean(msg._pending);
  const label = formatSupportSenderLabel(msg, { view: 'admin' });
  return (
    <div className={`flex gap-3 ${isAdmin || isInternal ? 'flex-row-reverse' : ''} ${pending ? 'opacity-60' : ''}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
        isInternal ? 'bg-amber-100 text-amber-800' : isAdmin ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
      }`}>
        {isInternal ? <Lock className="w-4 h-4" /> : isAdmin ? <Shield className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />}
      </div>
      <div className={`flex-1 max-w-[80%] ${isAdmin || isInternal ? 'text-right' : ''}`}>
        <div className="text-xs text-gray-500 mb-1">
          <strong className="text-gray-900">{label}</strong>
          <span className="mx-1.5">·</span>
          <span>{new Date(msg.created_at).toLocaleString()}</span>
        </div>
        <div className={`inline-block text-sm rounded-lg p-3 whitespace-pre-wrap text-left ${
          isInternal ? 'bg-amber-50 text-amber-950 border border-amber-200'
            : isAdmin ? 'bg-blue-50 text-gray-900' : 'bg-gray-100 text-gray-900'
        }`}>
          {msg.message}
          {msg.attachments?.length > 0 && (
            <div className="mt-2 space-y-1">
              {msg.attachments.map((a, i) => (
                <a key={i} href={supportAttachmentHref(ticketRef, a.url, true)} target="_blank" rel="noreferrer"
                   className="block text-xs text-blue-600 hover:underline flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> {a.filename}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportQueue;
